import moment from "moment";
import React, { useEffect, useState } from "react";
import { getTimeStringFromDate } from "../../../utils/timeFormatter";
import {
  DayClickEventHandler,
  MonthChangeEventHandler,
} from "react-day-picker";
import config from "../../../config";
import {
  AssertIsAfterOrEqualDate,
  AssertIsBeforeOrEqualDate,
} from "../../../utils/assert";

const STRICT_MATCHING = true;
const TODAY = new Date();

export const useInput = ({
  value,
  format = config.DATE_FORMAT,
  fromDate,
  toDate,
  placeholder,
  onChange,
  onBlur,
  isTouched,
  isTouchedRespected,
}: useInputProps) => {
  const [month, setMonth] = useState(value);
  const [selectedDay, setSelectedDay] = useState<Date>(value);

  const [inputValue, setInputValue] = useState(
    getTimeStringFromDate(value, config.DATE_FORMAT)
  );

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleDayClick: DayClickEventHandler = (day) => {
    setSelectedDay(day);
    setInputValue(day ? getTimeStringFromDate(day, format) : "");
  };

  const handleMonthChange: MonthChangeEventHandler = (month) => {
    setMonth(month);
  };

  // When changing the input field, save its value in state and check if the
  // string is a valid date. If it is a valid day, set it as selected and update
  // the calendar’s month.
  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const inputvalue = e.target.value;
    setInputValue(inputvalue);

    const day = moment(inputvalue, format, STRICT_MATCHING);

    const isBefore = fromDate && AssertIsBeforeOrEqualDate(day, fromDate);
    const isAfter = toDate && AssertIsAfterOrEqualDate(day, toDate);

    if (day.isValid() && !isBefore && !isAfter) {
      setMonth(day.toDate());
    }
    setSelectedDay(day.toDate() ?? undefined);
  };

  const handleInputClick = () => {
    setIsPopoverOpen(true);
  };

  const handleApplyclick = (event: React.MouseEvent) => {
    event.stopPropagation();

    setIsPopoverOpen(false);
    onBlur?.();

    if (moment(selectedDay, format).isValid()) {
      onChange?.(selectedDay);
    }
  };

  const handleCancelClick = () => {
    setIsPopoverOpen(false);
    onBlur?.();
  };

  // When popover closes, ensure the internal state controlling input/calendar
  // matches outside state
  useEffect(() => {
    if (!isPopoverOpen) {
      setSelectedDay(value);
      setMonth(value);
      setInputValue(getTimeStringFromDate(value, format));
    }
  }, [value, isPopoverOpen]);

  const dayPickerProps = {
    month: month,
    onDayClick: handleDayClick,
    onMonthChange: handleMonthChange,
    selected: selectedDay,
    fromDate,
    toDate,
  };

  const getValue = () => {
    if (isTouchedRespected) {
      return isTouched ? inputValue : undefined; // we can't use null but undefined does work in this context so that form appears empty
    }
    return inputValue;
  };

  const inputProps = {
    onChange: handleInputChange,
    onClick: handleInputClick,
    value: getValue(),
    placeholder: getTimeStringFromDate(placeholder ?? TODAY, format),
  };

  const popoverProps = {
    isOpen: isPopoverOpen,
    onClickOutside: handleCancelClick,
  };

  const footerProps = {
    onApplyClick: handleApplyclick,
    onCancelClick: handleCancelClick,
  };

  return { dayPickerProps, inputProps, popoverProps, footerProps };
};

type useInputProps = {
  value: Date;
  isTouched?: boolean;
  isTouchedRespected?: boolean;
  isEmpty?: boolean;
  fromDate?: Date;
  toDate?: Date;
  format?: string;
  placeholder?: string;
  onBlur?: () => void;
  onChange?: (date?: Date) => void;
};

export default useInput;
